import * as moment from "moment/moment";
import { IClient } from "../interfaces/client.interface";
const _ = require('lodash');
import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
const pako = require('pako');

import { inject } from '@angular/core';
import { CanActivateFn, ActivatedRouteSnapshot, Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from "../../environments/environment";

export const months = {
  '1': 'Ianuarie',
  '2': 'Februarie',
  '3': 'Martie',
  '4': 'Aprilie',
  '5': 'Mai',
  '6': 'Iunie',
  '7': 'Iulie',
  '8': 'August',
  '9': 'Septembrie',
  '10': 'Octombrie',
  '11': 'Noiembrie',
  '12': 'Decembrie'
};

export function parseNumberFilter(str) {
  const s = str.trim().replace(',', '.').replace(/\s+/g, '');

  const match = s.match(/^(>=|<=|>|<|=)\s*(-?\d+(\.\d+)?)$/);
  if (!match) return {
    operation: '=',
    value: s
  };

  return {
    operation: match[1],
    value: parseFloat(match[2])
  };
}

export function createSSEObservable<T>(url: string, headers: Record<string, string>): Observable<{ type: string; data: T }> {
  return new Observable((observer) => {
    const abortController = new AbortController();
    fetch(url, {
      headers,
      signal: abortController.signal,
    })
      .then((response) => {
        if (!response.body) {
          throw new Error('ReadableStream not supported.');
        }
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        const processText = ({ done, value }: ReadableStreamReadResult<Uint8Array>): any => {
          if (done) {
            observer.complete();
            return;
          }
          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';
          let currentEvent: string | null = null;
          for (let i = 0; i < lines.length; i++) {
            const line = lines[i];
            if (line.startsWith('event: ')) {
              currentEvent = line.substring(7).trim();
            } else if (line.startsWith('data: ') && currentEvent) {
              const data = line.substring(6);
              try {
                observer.next({
                  type: currentEvent,
                  data: JSON.parse(data),
                });
              } catch (e) {
                console.error('Failed to parse SSE data:', e);
              }
              currentEvent = null;
            } else if (line === '') {
              currentEvent = null;
            }
          }
          return reader.read().then(processText);
        };
        return reader.read().then(processText);
      })
      .catch((error) => observer.error(error));
    return () => abortController.abort();
  });
}

// Payment types: NUMERAR, CARD, BONURI, CEC, METODE MODERNE
export function generateXml(payments:{type:'numerar'|'card'|'bonuri', amount: number}[], invoice) {
  let xml = '<?xml version="1.0" encoding="utf-8"?>';
  xml += '<Factura>';
  xml += '<Client>'
      xml += '<Nume></Nume>'
      if (invoice?.cui){
        xml += `<CIF>${invoice?.cui}</CIF>`
      }else{
        xml += '<CIF></CIF>'
      }
      xml += '<Adresa></Adresa>'
    xml += '</Client>'
    xml += '<Bon>'
      xml += '<Operator></Operator>'
      xml += '<Incasare>'
      payments?.forEach(payment => {
        switch (payment.type) {
          case 'numerar':
            xml += `<Numerar>${payment.amount}</Numerar>`;
            break;
          case 'card':
            xml += `<Card>${payment.amount}</Card>`;
            break;
          default:
            break;
        }
      })
      xml += '</Incasare>'
    xml += '</Bon>'
    xml += '<Detalii>'
    invoice.items.forEach((item)=>{
      xml += '<linie>'
      xml += `<Cod>${item.code || item.articleId || ''}</Cod>`
      xml += `<Descriere>${item.name === 'Bacșiș' ? 'Bacsis' : item?.name}</Descriere>`
      xml += `<UM>${item.unitName || ''}</UM>`
      xml += `<TVA>${item.vat ?? ''}</TVA>`
      xml += `<Pret>${item.unitCost || ''}</Pret>` // UNIT PRICE!!
      xml += `<PretTVA>${item.unitCostWithVat || ''}</PretTVA>` // UNIT PRICE!!
      xml += `<Cantitate>${item.quantity || ''}</Cantitate>`
      xml += `<InformatiiSuplimentare></InformatiiSuplimentare>`
      xml += '</linie>'
    })
    xml += '</Detalii>'
  xml += '</Factura>'
  return xml;
}

export function generateBonObj(payments:{type:'numerar'|'card'|'bonuri', amount: number}[], invoice) {
  const bon = {
    numberFull: '',
    client: {
      name: '',
      cif: '',
      address: ''
    },
    payments: [],
    items: [],
  }
  if (!!invoice?.cui && typeof invoice.cui === 'string') {
    bon.client.cif = invoice.cui;
  }
  if (invoice?.numberFull) {
    bon.numberFull = invoice?.numberFull;
  }
  payments?.forEach(payment => {
    const paymentObj:any = {
      type: '',
      amount: '',
    };
    paymentObj.amount = payment.amount;
    switch (payment.type) {
      case 'numerar':
        paymentObj.type = 'cash';
        break;
      case 'card':
        paymentObj.type = 'card';
        break;
      default:
        break;
    }
    bon.payments.push(paymentObj);
  });
  invoice.items.forEach((item)=>{
    item.name = item.name === 'Bacșiș' ? 'Bacsis' : item.name;
    const itemObj = {
      code: item.code || item.articleId || '',
      name: item.name || '',
      um: item.unit || '',
      vat: item.vat || '',
      unitPrice: item.unitCost || '',
      unitPriceWithVat: item.unitCostWithVat || '',
      quantity: item.quantity || '',
      additionalInfo: '', 
    }
    bon.items.push(itemObj);
  })
  return bon;
}

function addVat(value: string, vat: number) {
  const valueNum = parseFloat(value);
  return (valueNum * (100 + parseFloat(vat.toString())) / 100).toFixed(2)
}

export function sendBonToPrinter(xml) {
  const href = 'saga:'+stringToGzipBase64(xml);
  const anchor = document.createElement("a");
  anchor.href = href;
  anchor.click();
  anchor.remove();
}

export function hasPermission(loggedUserPermissions, permissionCode: string): boolean {
  return loggedUserPermissions?.some(p => p.code === permissionCode) ?? false;
}

export function getFormatedCurrentDate() {
  const nowDate = new Date();
  const year = moment(nowDate).format('YYYY');
  const month = moment(nowDate).format('MM');
  const day = moment(nowDate).format('DD');
  return `${year}-${month}-${day}`;
}

export enum LogAction {
  anulareBon = 'anulareBon',
  anulareArticolBon = 'anulareArticolBon',
  generareBon = 'generareBon',
  operatiuneX = 'operatiuneX',
  operatiuneZ = 'operatiuneZ',
  operatiuneCashIn = 'operatiuneCashIn',
  operatiuneCashOut = 'operatiuneCashOut',
  operatiuneRestituireSgr = 'operatiuneRestituireSgr',
  confirmarePlataCard = 'confirmarePlataCard',
  adaugareBonAsteptare = 'adaugareBonAsteptare',
  scoatereBonDinAsteptare = 'scoatereBonDinAsteptare',
  anulareBonDinAsteptare = 'anulareBonDinAsteptare',
  anulareBonPrinRefreshPagina = 'anulareBonPrinRefreshPagina',
  anularebonPrinInchidereAplicatie = 'anularebonPrinInchidereAplicatie',
  registerCompanyInvoice = 'registerCompanyInvoice',
  updateCompanyInvoice = 'updateCompanyInvoice',
  deleteCompanyInvoice = 'deleteCompanyInvoice',
  cancelCompanyInvoice = 'cancelCompanyInvoice',
  exportSagaCompanyInvoices = 'exportSagaCompanyInvoices',
  sendInvoiceToAnaf = 'sendInvoiceToAnaf',
  registerCompanyInvoicePayment = 'registerCompanyInvoicePayment',
  updateCompanyInvoicePayment = 'updateCompanyInvoicePayment',
  deleteCompanyInvoicePayment = 'deleteCompanyInvoicePayment',
  exportSagaCompanyInvoicePayment = 'exportSagaCompanyInvoicePayment',
  deleteCompanyAccountPayable = 'deleteCompanyAccountPayable',
  exportSagaCompanyAccountPayable = 'exportSagaCompanyAccountPayable',
  registerCompanyAccountPayable = 'registerCompanyAccountPayable',
  updateCompanyAccountPayable = 'updateCompanyAccountPayable',
  linkAccountPayableItemToArticle = 'linkAccountPayableItemToArticle',
  linkNonGestiuneAccountPayableItem = 'linkNonGestiuneAccountPayableItem',
  editLinkAccountPayableItemToArticle = 'editLinkAccountPayableItemToArticle',
  editLinkNonGestiuneAccountPayableItem = 'editLinkNonGestiuneAccountPayableItem',
  unlinkReceptieGestiune = "unlinkReceptieGestiune",
  unlinkReceptieNonGestiune = "unlinkReceptieNonGestiune",
  editAviz = "editAviz",
  addAviz = "addAviz",
  deleteAviz = "deleteAviz",
  editOferta =  "editOferta",
  addOferta =  "addOferta",
  deleteOferta =  "deleteOferta",
  editProforma = "editProforma",
  addProforma = "addProforma",
  deleteProforma = "deleteProforma",
  editArticlePrice = "editArticlePrice",
  editBarcode = "editBarcode",
  editArticle = "editArticle",
  deleteArticle = "deleteArticle",
}

export enum LogActionNames {
  anulareBon = 'Anulare bon',
  anulareArticolBon = 'Anulare articol bon',
  generareBon = 'Generare bon',
  operatiuneX = 'Operațiune X',
  operatiuneZ = 'Operațiune Z',
  operatiuneCashIn = 'Operațiune depunere numerar',
  operatiuneCashOut = 'Operațiune retragere numerar',
  operatiuneRestituireSgr = 'Operațiune restituire SGR',
  confirmarePlataCard = 'Confirmare plată card',
  adaugareBonAsteptare = 'Adăugare bon în așteptare',
  scoatereBonDinAsteptare = 'Scoatere bon din așteptare',
  anulareBonDinAsteptare = 'Anulare bon din așteptare',
  anulareBonPrinRefreshPagina = 'Anulare bon prin refresh pagină',
  anularebonPrinInchidereAplicatie = 'Anulare bon prin închidere aplicație',
  registerCompanyInvoice = 'Înregistrare factură client',
  updateCompanyInvoice = 'Actualizare factură client',
  deleteCompanyInvoice = 'Ștergere factură client',
  cancelCompanyInvoice = 'Anulare factură client',
  sendInvoiceToAnaf = 'Încărcare eFactura ANAF',
  registerCompanyInvoicePayment = 'Înregistrare încasare factură client',
  updateCompanyInvoicePayment = 'Actualizare încasare factură client',
  deleteCompanyInvoicePayment = 'Ștergere încasare factură client',
  exportSagaCompanyInvoices = 'Export Saga facturi clienti',
  exportSagaCompanyInvoicePayment = 'Export Saga încasări facturi clienti',
  exportSagaCompanyAccountPayable = 'Export Saga facturi furnizor',
  deleteCompanyAccountPayable = 'Ștergere factură furnizor',
  registerCompanyAccountPayable = 'Înregistrare factură furnizor',
  updateCompanyAccountPayable = 'Actualizare factură furnizor',
  linkAccountPayableItemToArticle = 'Receptie articol gestiune',
  editLinkAccountPayableItemToArticle = 'Editare receptie articol gestiune',
  linkNonGestiuneAccountPayableItem = 'Receptie articol fără gestiune',
  editLinkNonGestiuneAccountPayableItem = 'Editare receptie articol fără gestiune',
  unlinkReceptieNonGestiune = "Anulare recepție fară gestiune",
  unlinkReceptieGestiune = "Anulare recepție cu gestiune",
  editAviz = "Actualizare Aviz",
  addAviz = "Adăugare Aviz",
  deleteAviz = "Ștergere Aviz",
  editOferta =  "Actualizare Oferta",
  addOferta =  "Adăugare Oferta",
  deleteOferta =  "Ștergere Oferta",
  editProforma = "Actualizare Proforma",
  addProforma = "Adăugare Proforma",
  deleteProforma = "Ștergere Proforma",
  editArticlePrice = "Modificare preț articol",
  editBarcode = "Modificare cod de bare",
  editArticle = "Modificare articol",
  deleteArticle = "Ștergere articol",
}

export enum LogResourceType {
  bon = 'bon',
  invoice = 'invoice',
  invoicePayments = 'invoicePayments',
  accountPayable = 'accountPayable',
  articles = 'articles',
  receptie = 'receptie',
  export = 'export',
  posOperation = 'posOperation',
  aviz = 'aviz',
  oferta = 'oferta',
  proforma = "proforma"
}

export enum LogResourceTypeNames {
  bon = 'Bon',
  invoice = 'Factură client',
  invoicePayments = 'Încasare factura',
  accountPayable = 'Factură furnizor',
  articles = 'Articole',
  receptie = 'Recepție',
  export = 'Export',
  posOperation = 'Operație POS',
  aviz  = "Aviz",
  oferta = 'Oferta',
  proforma = "Proforma",
}

export function extractAddressFromAnaf(found: any, regions= []) {
  const address = found?.date_generale?.adresa;
  let client: IClient = {
    country: 'ROMANIA'
  };

  const addressParts = address.split(', ');
  addressParts.forEach(part => {
    if (part.includes('JUD.')) {
      const regionParts = part.split(' ');
      regionParts.shift();
      if (regions.length > 0) {
        const region = regions.find(reg => {
          let formatedRegion = _.toLower(replaceRomanianSpecialChars(reg.name));
          let formatedAnafRegion = _.toLower(replaceRomanianSpecialChars(regionParts.join(' ')))
          return formatedRegion === formatedAnafRegion;
        })
        client.region = region?.name;
        client.regionCode = region?.code;
      } else {
        client.region = regionParts.join(' ');
        client.regionCode = '';
      }
    } else if (part.includes('STR.')) {
      client.street = part;
    } else if (part.includes('SPL.')) {
      client.street = part;
    } else if (part.includes('CAL.')) {
      client.street = part;
    } else if (part.includes('CALEA')) {
      client.street = part;
    } else if (part.includes('BDUL.')) {
      client.street = part;
    } else if (part.includes('BLD.')) {
      client.street = part;
    } else if (part.includes('ALEEA')) {
      client.street = part;
    } else if (part.includes('PŢA.')) {
      client.street = part;
    } else if (part.includes('SAT')) {
      client.city = part;
    } else if (part.includes('ORŞ.')) {
      client.city = part;
    } else if (part.includes('MUNICIPIUL')) {
      client.city = part;
    } else if (part.includes('MUN')) {
      client.city = part;
    } else if (part.includes('COM.')) {
      client.city = part;
    } else if (part.includes('MUN.')) {
      client.city = part;
    } else if (part.includes('NR.')) {
      const numberParts = part.split('.');
      client.nr = numberParts[1];
    } else if (part.includes('BL.')) {
      const blockParts = part.split('.');
      client.bl = blockParts[1];
    } else if (part.includes('ET.')) {
      const floorParts = part.split('.');
      client.et = floorParts[1];
    } else if (part.includes('SC.')) {
      const scaraParts = part.split('.');
      client.sc = scaraParts[1];
    } else if (part.includes('AP.')) {
      const apartmentParts = part.split('.');
      client.ap = apartmentParts[1];
    } else if (part.includes('SECTOR')) {
      const sectorParts = part.split(' ');
      client.sector = sectorParts[1];
    } else if (part.includes('CAMERA')) {
      client.camera = part;
    } else if (part.includes('CAM.')) {
      client.camera = part;
    }
  });

  return client;
}

export function replaceRomanianSpecialChars(value: string) {
  value = _.replace(value, '&', '');
  value = _.replace(value, 'Ș', 'S');
  value = _.replace(value, 'Ş', 'S');
  value = _.replace(value, 'Ț', 'T');
  value = _.replace(value, 'Ţ', 'T');
  value = _.replace(value, 'Ă', 'A');
  value = _.replace(value, 'Ă', 'A');
  value = _.replace(value, 'Â', 'A');
  value = _.replace(value, 'Â', 'A');
  value = _.replace(value, 'Î', 'I');
  value = _.replace(value, 'Î', 'I');


  value = _.replace(value, 'ş', 's');
  value = _.replace(value, 'ș', 's');
  value = _.replace(value, 'ţ', 't');
  value = _.replace(value, 'ț', 't');
  value = _.replace(value, 'ă', 'a');
  value = _.replace(value, 'ă', 'a');
  value = _.replace(value, 'â', 'a');
  value = _.replace(value, 'â', 'a');
  value = _.replace(value, 'î', 'i');
  value = _.replace(value, 'î', 'i');
  value = _.replace(value, 'é', 'e');
  value = _.replace(value, '&', '');
  return value;
}

export function valueInListValidator(validList: any[]): ValidatorFn {
  return (control: AbstractControl) => {
    if (!control.value || validList.includes(control.value)) {
      return null;
    }
    return { valueNotInList: { value: control.value } };
  };
}

export function valueNotInListValidator(list: any[]): ValidatorFn {
  return (control: AbstractControl) => {
    if (!control.value || !list.includes(control.value)) {
      return null;
    }
    return { valueNotInList: { value: control.value } };
  }
}


export function stringToGzipBase64(string){
  const compressed = pako.gzip(string);
  return btoa(String.fromCharCode.apply(null, compressed));
}

export function prepareClientForSavingToDb(data, client, users) {
  data.search = `${data.name} ${data.legalForm} ${data.fiscalCode} ${data.specialCode} ${data.cnp}
      ${data.email} ${data.phone} ${data.country} ${data.region} ${data.regionCode} ${data.sector} ${data.city} ${data.address} ${data.postalCode}
      numar ${data.nr} strada ${data.street} bloc ${data.bl} scara ${data.sc} etaj ${data.et} apartament ${data.ap}
      ${data.nrRegCom} ${data.enrolmentDate} ${data.partners} ${data.administrator} ${data.bank}
    ${data.iban} ${data.observations} ${data.accountingPolicies} ${data.affiliation} ${data.ceccar} ${data.contractNumberFull}`;

  const user = users?.find(u => u.user_id === data.responsible_user_id);
  if (user && user?.teams.length > 0) {
    let team;
    if (!client) {
      team = user?.teams[0];
      client = {};
    } else {
      team = user?.teams?.find(t => t.id === client?.responsible_team_id);
    }
    
    if (team) {
      client.teamName = team.user?.user_firstname;
      client.user_firstname = user?.user_firstname;
      data.teamName = team.user?.user_firstname;
      data.user_firstname = user?.user_firstname;
    }
  }
  return {data, client};
}

export const mappingUnitCodes = {
  "PCS": "H87",
  "BUC": "H87"
}

export const countries = [
  { value: "AD", description: "Andorra" },
  { value: "AE", description: "Emiratele Arabe Unite" },
  { value: "AF", description: "Afganistan" },
  { value: "AG", description: "Antigua și Barbuda" },
  { value: "AI", description: "Anguilla" },
  { value: "AL", description: "Albania" },
  { value: "AM", description: "Armenia" },
  { value: "AO", description: "Angola" },
  { value: "AQ", description: "Antarctica" },
  { value: "AR", description: "Argentina" },
  { value: "AS", description: "Samoa Americană" },
  { value: "AT", description: "Austria" },
  { value: "AU", description: "Australia" },
  { value: "AW", description: "Aruba" },
  { value: "AX", description: "Insulele Åland" },
  { value: "AZ", description: "Azerbaidjan" },
  { value: "BA", description: "Bosnia și Herțegovina" },
  { value: "BB", description: "Barbados" },
  { value: "BD", description: "Bangladesh" },
  { value: "BE", description: "Belgia" },
  { value: "BF", description: "Burkina Faso" },
  { value: "BG", description: "Bulgaria" },
  { value: "BH", description: "Bahrain" },
  { value: "BI", description: "Burundi" },
  { value: "BJ", description: "Benin" },
  { value: "BL", description: "Saint Barthélemy" },
  { value: "BM", description: "Bermude" },
  { value: "BN", description: "Brunei" },
  { value: "BO", description: "Bolivia" },
  { value: "BQ", description: "Insulele Caraibe Olandeze" },
  { value: "BR", description: "Brazilia" },
  { value: "BS", description: "Bahamas" },
  { value: "BT", description: "Bhutan" },
  { value: "BV", description: "Insula Bouvet" },
  { value: "BW", description: "Botswana" },
  { value: "BY", description: "Belarus" },
  { value: "BZ", description: "Belize" },
  { value: "CA", description: "Canada" },
  { value: "CC", description: "Insulele Cocos" },
  { value: "CD", description: "Republica Democratică Congo" },
  { value: "CF", description: "Republica Centrafricană" },
  { value: "CG", description: "Congo" },
  { value: "CH", description: "Elveția" },
  { value: "CI", description: "Côte d'Ivoire" },
  { value: "CK", description: "Insulele Cook" },
  { value: "CL", description: "Chile" },
  { value: "CM", description: "Camerun" },
  { value: "CN", description: "China" },
  { value: "CO", description: "Columbia" },
  { value: "CR", description: "Costa Rica" },
  { value: "CU", description: "Cuba" },
  { value: "CV", description: "Capul Verde" },
  { value: "CW", description: "Curaçao" },
  { value: "CX", description: "Insula Christmas" },
  { value: "CY", description: "Cipru" },
  { value: "CZ", description: "Cehia" },
  { value: "DE", description: "Germania" },
  { value: "DJ", description: "Djibouti" },
  { value: "DK", description: "Danemarca" },
  { value: "DM", description: "Dominica" },
  { value: "DO", description: "Republica Dominicană" },
  { value: "DZ", description: "Algeria" },
  { value: "EC", description: "Ecuador" },
  { value: "EE", description: "Estonia" },
  { value: "EG", description: "Egipt" },
  { value: "EH", description: "Sahara Occidentală" },
  { value: "EL", description: "Grecia" },
  { value: "ER", description: "Eritreea" },
  { value: "ES", description: "Spania" },
  { value: "ET", description: "Etiopia" },
  { value: "FI", description: "Finlanda" },
  { value: "FJ", description: "Fiji" },
  { value: "FK", description: "Insulele Falkland" },
  { value: "FM", description: "Micronezia" },
  { value: "FO", description: "Insulele Feroe" },
  { value: "FR", description: "Franța" },
  { value: "GA", description: "Gabon" },
  { value: "GB", description: "Marea Britanie" },
  { value: "GD", description: "Grenada" },
  { value: "GE", description: "Georgia" },
  { value: "GF", description: "Guiana Franceză" },
  { value: "GG", description: "Guernsey" },
  { value: "GH", description: "Ghana" },
  { value: "GI", description: "Gibraltar" },
  { value: "GL", description: "Groenlanda" },
  { value: "GM", description: "Gambia" },
  { value: "GN", description: "Guineea" },
  { value: "GP", description: "Guadelupa" },
  { value: "GQ", description: "Guineea Ecuatorială" },
  { value: "GS", description: "Georgia de Sud și Insulele Sandwich de Sud" },
  { value: "GT", description: "Guatemala" },
  { value: "GU", description: "Guam" },
  { value: "GW", description: "Guineea-Bissau" },
  { value: "GY", description: "Guyana" },
  { value: "HK", description: "Hong Kong" },
  { value: "HM", description: "Insulele Heard și McDonald" },
  { value: "HN", description: "Honduras" },
  { value: "HR", description: "Croația" },
  { value: "HT", description: "Haiti" },
  { value: "HU", description: "Ungaria" },
  { value: "ID", description: "Indonezia" },
  { value: "IE", description: "Irlanda" },
  { value: "IL", description: "Israel" },
  { value: "IM", description: "Insula Man" },
  { value: "IN", description: "India" },
  { value: "IO", description: "Teritoriul Britanic din Oceanul Indian" },
  { value: "IQ", description: "Irak" },
  { value: "IR", description: "Iran" },
  { value: "IS", description: "Islanda" },
  { value: "IT", description: "Italia" },
  { value: "JE", description: "Jersey" },
  { value: "JM", description: "Jamaica" },
  { value: "JO", description: "Iordania" },
  { value: "JP", description: "Japonia" },
  { value: "KE", description: "Kenya" },
  { value: "KG", description: "Kârgâzstan" },
  { value: "KH", description: "Cambodgia" },
  { value: "KI", description: "Kiribati" },
  { value: "KM", description: "Comore" },
  { value: "KN", description: "Saint Kitts și Nevis" },
  { value: "KP", description: "Coreea de Nord" },
  { value: "KR", description: "Coreea de Sud" },
  { value: "KW", description: "Kuweit" },
  { value: "KY", description: "Insulele Cayman" },
  { value: "KZ", description: "Kazahstan" },
  { value: "LA", description: "Laos" },
  { value: "LB", description: "Liban" },
  { value: "LC", description: "Saint Lucia" },
  { value: "LI", description: "Liechtenstein" },
  { value: "LK", description: "Sri Lanka" },
  { value: "LR", description: "Liberia" },
  { value: "LS", description: "Lesotho" },
  { value: "LT", description: "Lituania" },
  { value: "LU", description: "Luxemburg" },
  { value: "LV", description: "Letonia" },
  { value: "LY", description: "Libia" },
  { value: "MA", description: "Maroc" },
  { value: "MC", description: "Monaco" },
  { value: "MD", description: "Moldova" },
  { value: "ME", description: "Muntenegru" },
  { value: "MF", description: "Saint Martin" },
  { value: "MG", description: "Madagascar" },
  { value: "MH", description: "Insulele Marshall" },
  { value: "MK", description: "Macedonia de Nord" },
  { value: "ML", description: "Mali" },
  { value: "MM", description: "Myanmar" },
  { value: "MN", description: "Mongolia" },
  { value: "MO", description: "Macao" },
  { value: "MP", description: "Insulele Mariane de Nord" },
  { value: "MQ", description: "Martinica" },
  { value: "MR", description: "Mauritania" },
  { value: "MS", description: "Montserrat" },
  { value: "MT", description: "Malta" },
  { value: "MU", description: "Mauritius" },
  { value: "MV", description: "Maldive" },
  { value: "MW", description: "Malawi" },
  { value: "MX", description: "Mexic" },
  { value: "MY", description: "Malaezia" },
  { value: "MZ", description: "Mozambic" },
  { value: "NA", description: "Namibia" },
  { value: "NC", description: "Noua Caledonie" },
  { value: "NE", description: "Niger" },
  { value: "NF", description: "Insula Norfolk" },
  { value: "NG", description: "Nigeria" },
  { value: "NI", description: "Nicaragua" },
  { value: "NL", description: "Olanda" },
  { value: "NO", description: "Norvegia" },
  { value: "NP", description: "Nepal" },
  { value: "NR", description: "Nauru" },
  { value: "NU", description: "Niue" },
  { value: "NZ", description: "Noua Zeelandă" },
  { value: "OM", description: "Oman" },
  { value: "PA", description: "Panama" },
  { value: "PE", description: "Peru" },
  { value: "PF", description: "Polinezia Franceză" },
  { value: "PG", description: "Papua Noua Guinee" },
  { value: "PH", description: "Filipine" },
  { value: "PK", description: "Pakistan" },
  { value: "PL", description: "Polonia" },
  { value: "PM", description: "Saint Pierre și Miquelon" },
  { value: "PN", description: "Insulele Pitcairn" },
  { value: "PR", description: "Puerto Rico" },
  { value: "PS", description: "Palestina" },
  { value: "PT", description: "Portugalia" },
  { value: "PW", description: "Palau" },
  { value: "PY", description: "Paraguay" },
  { value: "QA", description: "Qatar" },
  { value: "RE", description: "Réunion" },
  { value: "RO", description: "România" },
  { value: "RS", description: "Serbia" },
  { value: "RU", description: "Rusia" },
  { value: "RW", description: "Rwanda" },
  { value: "SA", description: "Arabia Saudită" },
  { value: "SB", description: "Insulele Solomon" },
  { value: "SC", description: "Seychelles" },
  { value: "SD", description: "Sudan" },
  { value: "SE", description: "Suedia" },
  { value: "SG", description: "Singapore" },
  { value: "SH", description: "Sfânta Elena" },
  { value: "SI", description: "Slovenia" },
  { value: "SJ", description: "Svalbard și Jan Mayen" },
  { value: "SK", description: "Slovacia" },
  { value: "SL", description: "Sierra Leone" },
  { value: "SM", description: "San Marino" },
  { value: "SN", description: "Senegal" },
  { value: "SO", description: "Somalia" },
  { value: "SR", description: "Surinam" },
  { value: "SS", description: "Sudanul de Sud" },
  { value: "ST", description: "Sao Tome și Principe" },
  { value: "SV", description: "El Salvador" },
  { value: "SX", description: "Sint Maarten" },
  { value: "SY", description: "Siria" },
  { value: "SZ", description: "Eswatini" },
  { value: "TC", description: "Insulele Turks și Caicos" },
  { value: "TD", description: "Ciad" },
  { value: "TF", description: "Teritoriile Australe și Antarctice Franceze" },
  { value: "TG", description: "Togo" },
  { value: "TH", description: "Thailanda" },
  { value: "TJ", description: "Tadjikistan" },
  { value: "TK", description: "Tokelau" },
  { value: "TL", description: "Timorul de Est" },
  { value: "TM", description: "Turkmenistan" },
  { value: "TN", description: "Tunisia" },
  { value: "TO", description: "Tonga" },
  { value: "TR", description: "Turcia" },
  { value: "TT", description: "Trinidad și Tobago" },
  { value: "TV", description: "Tuvalu" },
  { value: "TW", description: "Taiwan" },
  { value: "TZ", description: "Tanzania" },
  { value: "UA", description: "Ucraina" },
  { value: "UG", description: "Uganda" },
  { value: "UM", description: "Insulele Minore Îndepărtate ale SUA" },
  { value: "US", description: "SUA" },
  { value: "UY", description: "Uruguay" },
  { value: "UZ", description: "Uzbekistan" },
  { value: "VA", description: "Vatican" },
  { value: "VC", description: "Saint Vincent și Grenadinele" },
  { value: "VE", description: "Venezuela" },
  { value: "VG", description: "Insulele Virgine Britanice" },
  { value: "VI", description: "Insulele Virgine Americane" },
  { value: "VN", description: "Vietnam" },
  { value: "VU", description: "Vanuatu" },
  { value: "WF", description: "Wallis și Futuna" },
  { value: "WS", description: "Samoa" },
  { value: "XI", description: "Irlanda de Nord (protocolul Brexit)" },
  { value: "YE", description: "Yemen" },
  { value: "YT", description: "Mayotte" },
  { value: "ZA", description: "Africa de Sud" },
  { value: "ZM", description: "Zambia" },
  { value: "ZW", description: "Zimbabwe" }
]

const euCountryCodes = [
  "AT", // Austria
  "BE", // Belgium
  "BG", // Bulgaria
  "HR", // Croatia
  "CY", // Cyprus
  "CZ", // Czechia
  "DK", // Denmark
  "EE", // Estonia
  "FI", // Finland
  "FR", // France
  "DE", // Germany
  "EL", // Greece
  "HU", // Hungary
  "IE", // Ireland
  "IT", // Italy
  "LV", // Latvia
  "LT", // Lithuania
  "LU", // Luxembourg
  "MT", // Malta
  "NL", // Netherlands
  "PL", // Poland
  "PT", // Portugal
  "RO", // Romania
  "SK", // Slovakia
  "SI", // Slovenia
  "ES", // Spain
  "SE"  // Sweden
];

export const nonEuCountries = countries.filter(
  country => !euCountryCodes.includes(country.value)
);

export const sortedCountries = countries.sort((a, b) => {
  if (a.description < b.description) {
      return -1;
  }
  if (a.description > b.description) {
      return 1;
  }
  return 0;
});

const latinMap = {
  "Á":"A",
  "Ă":"A",
  "Ắ":"A",
  "Ặ":"A",
  "Ằ":"A",
  "Ẳ":"A",
  "Ẵ":"A",
  "Ǎ":"A",
  "Â":"A",
  "Ấ":"A",
  "Ậ":"A",
  "Ầ":"A",
  "Ẩ":"A",
  "Ẫ":"A",
  "Ä":"A",
  "Ǟ":"A",
  "Ȧ":"A",
  "Ǡ":"A",
  "Ạ":"A",
  "Ȁ":"A",
  "À":"A",
  "Ả":"A",
  "Ȃ":"A",
  "Ā":"A",
  "Ą":"A",
  "Å":"A",
  "Ǻ":"A",
  "Ḁ":"A",
  "Ⱥ":"A",
  "Ã":"A",
  "Ꜳ":"AA",
  "Æ":"AE",
  "Ǽ":"AE",
  "Ǣ":"AE",
  "Ꜵ":"AO",
  "Ꜷ":"AU",
  "Ꜹ":"AV",
  "Ꜻ":"AV",
  "Ꜽ":"AY",
  "Ḃ":"B",
  "Ḅ":"B",
  "Ɓ":"B",
  "Ḇ":"B",
  "Ƀ":"B",
  "Ƃ":"B",
  "Ć":"C",
  "Č":"C",
  "Ç":"C",
  "Ḉ":"C",
  "Ĉ":"C",
  "Ċ":"C",
  "Ƈ":"C",
  "Ȼ":"C",
  "Ď":"D",
  "Ḑ":"D",
  "Ḓ":"D",
  "Ḋ":"D",
  "Ḍ":"D",
  "Ɗ":"D",
  "Ḏ":"D",
  "ǲ":"D",
  "ǅ":"D",
  "Đ":"D",
  "Ƌ":"D",
  "Ǳ":"DZ",
  "Ǆ":"DZ",
  "É":"E",
  "Ĕ":"E",
  "Ě":"E",
  "Ȩ":"E",
  "Ḝ":"E",
  "Ê":"E",
  "Ế":"E",
  "Ệ":"E",
  "Ề":"E",
  "Ể":"E",
  "Ễ":"E",
  "Ḙ":"E",
  "Ë":"E",
  "Ė":"E",
  "Ẹ":"E",
  "Ȅ":"E",
  "È":"E",
  "Ẻ":"E",
  "Ȇ":"E",
  "Ē":"E",
  "Ḗ":"E",
  "Ḕ":"E",
  "Ę":"E",
  "Ɇ":"E",
  "Ẽ":"E",
  "Ḛ":"E",
  "Ꝫ":"ET",
  "Ḟ":"F",
  "Ƒ":"F",
  "Ǵ":"G",
  "Ğ":"G",
  "Ǧ":"G",
  "Ģ":"G",
  "Ĝ":"G",
  "Ġ":"G",
  "Ɠ":"G",
  "Ḡ":"G",
  "Ǥ":"G",
  "Ḫ":"H",
  "Ȟ":"H",
  "Ḩ":"H",
  "Ĥ":"H",
  "Ⱨ":"H",
  "Ḧ":"H",
  "Ḣ":"H",
  "Ḥ":"H",
  "Ħ":"H",
  "Í":"I",
  "Ĭ":"I",
  "Ǐ":"I",
  "Î":"I",
  "Ï":"I",
  "Ḯ":"I",
  "İ":"I",
  "Ị":"I",
  "Ȉ":"I",
  "Ì":"I",
  "Ỉ":"I",
  "Ȋ":"I",
  "Ī":"I",
  "Į":"I",
  "Ɨ":"I",
  "Ĩ":"I",
  "Ḭ":"I",
  "Ꝺ":"D",
  "Ꝼ":"F",
  "Ᵹ":"G",
  "Ꞃ":"R",
  "Ꞅ":"S",
  "Ꞇ":"T",
  "Ꝭ":"IS",
  "Ĵ":"J",
  "Ɉ":"J",
  "Ḱ":"K",
  "Ǩ":"K",
  "Ķ":"K",
  "Ⱪ":"K",
  "Ꝃ":"K",
  "Ḳ":"K",
  "Ƙ":"K",
  "Ḵ":"K",
  "Ꝁ":"K",
  "Ꝅ":"K",
  "Ĺ":"L",
  "Ƚ":"L",
  "Ľ":"L",
  "Ļ":"L",
  "Ḽ":"L",
  "Ḷ":"L",
  "Ḹ":"L",
  "Ⱡ":"L",
  "Ꝉ":"L",
  "Ḻ":"L",
  "Ŀ":"L",
  "Ɫ":"L",
  "ǈ":"L",
  "Ł":"L",
  "Ǉ":"LJ",
  "Ḿ":"M",
  "Ṁ":"M",
  "Ṃ":"M",
  "Ɱ":"M",
  "Ń":"N",
  "Ň":"N",
  "Ņ":"N",
  "Ṋ":"N",
  "Ṅ":"N",
  "Ṇ":"N",
  "Ǹ":"N",
  "Ɲ":"N",
  "Ṉ":"N",
  "Ƞ":"N",
  "ǋ":"N",
  "Ñ":"N",
  "Ǌ":"NJ",
  "Ó":"O",
  "Ŏ":"O",
  "Ǒ":"O",
  "Ô":"O",
  "Ố":"O",
  "Ộ":"O",
  "Ồ":"O",
  "Ổ":"O",
  "Ỗ":"O",
  "Ö":"O",
  "Ȫ":"O",
  "Ȯ":"O",
  "Ȱ":"O",
  "Ọ":"O",
  "Ő":"O",
  "Ȍ":"O",
  "Ò":"O",
  "Ỏ":"O",
  "Ơ":"O",
  "Ớ":"O",
  "Ợ":"O",
  "Ờ":"O",
  "Ở":"O",
  "Ỡ":"O",
  "Ȏ":"O",
  "Ꝋ":"O",
  "Ꝍ":"O",
  "Ō":"O",
  "Ṓ":"O",
  "Ṑ":"O",
  "Ɵ":"O",
  "Ǫ":"O",
  "Ǭ":"O",
  "Ø":"O",
  "Ǿ":"O",
  "Õ":"O",
  "Ṍ":"O",
  "Ṏ":"O",
  "Ȭ":"O",
  "Ƣ":"OI",
  "Ꝏ":"OO",
  "Ɛ":"E",
  "Ɔ":"O",
  "Ȣ":"OU",
  "Ṕ":"P",
  "Ṗ":"P",
  "Ꝓ":"P",
  "Ƥ":"P",
  "Ꝕ":"P",
  "Ᵽ":"P",
  "Ꝑ":"P",
  "Ꝙ":"Q",
  "Ꝗ":"Q",
  "Ŕ":"R",
  "Ř":"R",
  "Ŗ":"R",
  "Ṙ":"R",
  "Ṛ":"R",
  "Ṝ":"R",
  "Ȑ":"R",
  "Ȓ":"R",
  "Ṟ":"R",
  "Ɍ":"R",
  "Ɽ":"R",
  "Ꜿ":"C",
  "Ǝ":"E",
  "Ś":"S",
  "Ṥ":"S",
  "Š":"S",
  "Ṧ":"S",
  "Ş":"S",
  "Ŝ":"S",
  "Ș":"S",
  "Ṡ":"S",
  "Ṣ":"S",
  "Ṩ":"S",
  "Ť":"T",
  "Ţ":"T",
  "Ṱ":"T",
  "Ț":"T",
  "Ⱦ":"T",
  "Ṫ":"T",
  "Ṭ":"T",
  "Ƭ":"T",
  "Ṯ":"T",
  "Ʈ":"T",
  "Ŧ":"T",
  "Ɐ":"A",
  "Ꞁ":"L",
  "Ɯ":"M",
  "Ʌ":"V",
  "Ꜩ":"TZ",
  "Ú":"U",
  "Ŭ":"U",
  "Ǔ":"U",
  "Û":"U",
  "Ṷ":"U",
  "Ü":"U",
  "Ǘ":"U",
  "Ǚ":"U",
  "Ǜ":"U",
  "Ǖ":"U",
  "Ṳ":"U",
  "Ụ":"U",
  "Ű":"U",
  "Ȕ":"U",
  "Ù":"U",
  "Ủ":"U",
  "Ư":"U",
  "Ứ":"U",
  "Ự":"U",
  "Ừ":"U",
  "Ử":"U",
  "Ữ":"U",
  "Ȗ":"U",
  "Ū":"U",
  "Ṻ":"U",
  "Ų":"U",
  "Ů":"U",
  "Ũ":"U",
  "Ṹ":"U",
  "Ṵ":"U",
  "Ꝟ":"V",
  "Ṿ":"V",
  "Ʋ":"V",
  "Ṽ":"V",
  "Ꝡ":"VY",
  "Ẃ":"W",
  "Ŵ":"W",
  "Ẅ":"W",
  "Ẇ":"W",
  "Ẉ":"W",
  "Ẁ":"W",
  "Ⱳ":"W",
  "Ẍ":"X",
  "Ẋ":"X",
  "Ý":"Y",
  "Ŷ":"Y",
  "Ÿ":"Y",
  "Ẏ":"Y",
  "Ỵ":"Y",
  "Ỳ":"Y",
  "Ƴ":"Y",
  "Ỷ":"Y",
  "Ỿ":"Y",
  "Ȳ":"Y",
  "Ɏ":"Y",
  "Ỹ":"Y",
  "Ź":"Z",
  "Ž":"Z",
  "Ẑ":"Z",
  "Ⱬ":"Z",
  "Ż":"Z",
  "Ẓ":"Z",
  "Ȥ":"Z",
  "Ẕ":"Z",
  "Ƶ":"Z",
  "Ĳ":"IJ",
  "Œ":"OE",
  "ᴀ":"A",
  "ᴁ":"AE",
  "ʙ":"B",
  "ᴃ":"B",
  "ᴄ":"C",
  "ᴅ":"D",
  "ᴇ":"E",
  "ꜰ":"F",
  "ɢ":"G",
  "ʛ":"G",
  "ʜ":"H",
  "ɪ":"I",
  "ʁ":"R",
  "ᴊ":"J",
  "ᴋ":"K",
  "ʟ":"L",
  "ᴌ":"L",
  "ᴍ":"M",
  "ɴ":"N",
  "ᴏ":"O",
  "ɶ":"OE",
  "ᴐ":"O",
  "ᴕ":"OU",
  "ᴘ":"P",
  "ʀ":"R",
  "ᴎ":"N",
  "ᴙ":"R",
  "ꜱ":"S",
  "ᴛ":"T",
  "ⱻ":"E",
  "ᴚ":"R",
  "ᴜ":"U",
  "ᴠ":"V",
  "ᴡ":"W",
  "ʏ":"Y",
  "ᴢ":"Z",
  "á":"a",
  "ă":"a",
  "ắ":"a",
  "ặ":"a",
  "ằ":"a",
  "ẳ":"a",
  "ẵ":"a",
  "ǎ":"a",
  "â":"a",
  "ấ":"a",
  "ậ":"a",
  "ầ":"a",
  "ẩ":"a",
  "ẫ":"a",
  "ä":"a",
  "ǟ":"a",
  "ȧ":"a",
  "ǡ":"a",
  "ạ":"a",
  "ȁ":"a",
  "à":"a",
  "ả":"a",
  "ȃ":"a",
  "ā":"a",
  "ą":"a",
  "ᶏ":"a",
  "ẚ":"a",
  "å":"a",
  "ǻ":"a",
  "ḁ":"a",
  "ⱥ":"a",
  "ã":"a",
  "ꜳ":"aa",
  "æ":"ae",
  "ǽ":"ae",
  "ǣ":"ae",
  "ꜵ":"ao",
  "ꜷ":"au",
  "ꜹ":"av",
  "ꜻ":"av",
  "ꜽ":"ay",
  "ḃ":"b",
  "ḅ":"b",
  "ɓ":"b",
  "ḇ":"b",
  "ᵬ":"b",
  "ᶀ":"b",
  "ƀ":"b",
  "ƃ":"b",
  "ɵ":"o",
  "ć":"c",
  "č":"c",
  "ç":"c",
  "ḉ":"c",
  "ĉ":"c",
  "ɕ":"c",
  "ċ":"c",
  "ƈ":"c",
  "ȼ":"c",
  "ď":"d",
  "ḑ":"d",
  "ḓ":"d",
  "ȡ":"d",
  "ḋ":"d",
  "ḍ":"d",
  "ɗ":"d",
  "ᶑ":"d",
  "ḏ":"d",
  "ᵭ":"d",
  "ᶁ":"d",
  "đ":"d",
  "ɖ":"d",
  "ƌ":"d",
  "ı":"i",
  "ȷ":"j",
  "ɟ":"j",
  "ʄ":"j",
  "ǳ":"dz",
  "ǆ":"dz",
  "é":"e",
  "ĕ":"e",
  "ě":"e",
  "ȩ":"e",
  "ḝ":"e",
  "ê":"e",
  "ế":"e",
  "ệ":"e",
  "ề":"e",
  "ể":"e",
  "ễ":"e",
  "ḙ":"e",
  "ë":"e",
  "ė":"e",
  "ẹ":"e",
  "ȅ":"e",
  "è":"e",
  "ẻ":"e",
  "ȇ":"e",
  "ē":"e",
  "ḗ":"e",
  "ḕ":"e",
  "ⱸ":"e",
  "ę":"e",
  "ᶒ":"e",
  "ɇ":"e",
  "ẽ":"e",
  "ḛ":"e",
  "ꝫ":"et",
  "ḟ":"f",
  "ƒ":"f",
  "ᵮ":"f",
  "ᶂ":"f",
  "ǵ":"g",
  "ğ":"g",
  "ǧ":"g",
  "ģ":"g",
  "ĝ":"g",
  "ġ":"g",
  "ɠ":"g",
  "ḡ":"g",
  "ᶃ":"g",
  "ǥ":"g",
  "ḫ":"h",
  "ȟ":"h",
  "ḩ":"h",
  "ĥ":"h",
  "ⱨ":"h",
  "ḧ":"h",
  "ḣ":"h",
  "ḥ":"h",
  "ɦ":"h",
  "ẖ":"h",
  "ħ":"h",
  "ƕ":"hv",
  "í":"i",
  "ĭ":"i",
  "ǐ":"i",
  "î":"i",
  "ï":"i",
  "ḯ":"i",
  "ị":"i",
  "ȉ":"i",
  "ì":"i",
  "ỉ":"i",
  "ȋ":"i",
  "ī":"i",
  "į":"i",
  "ᶖ":"i",
  "ɨ":"i",
  "ĩ":"i",
  "ḭ":"i",
  "ꝺ":"d",
  "ꝼ":"f",
  "ᵹ":"g",
  "ꞃ":"r",
  "ꞅ":"s",
  "ꞇ":"t",
  "ꝭ":"is",
  "ǰ":"j",
  "ĵ":"j",
  "ʝ":"j",
  "ɉ":"j",
  "ḱ":"k",
  "ǩ":"k",
  "ķ":"k",
  "ⱪ":"k",
  "ꝃ":"k",
  "ḳ":"k",
  "ƙ":"k",
  "ḵ":"k",
  "ᶄ":"k",
  "ꝁ":"k",
  "ꝅ":"k",
  "ĺ":"l",
  "ƚ":"l",
  "ɬ":"l",
  "ľ":"l",
  "ļ":"l",
  "ḽ":"l",
  "ȴ":"l",
  "ḷ":"l",
  "ḹ":"l",
  "ⱡ":"l",
  "ꝉ":"l",
  "ḻ":"l",
  "ŀ":"l",
  "ɫ":"l",
  "ᶅ":"l",
  "ɭ":"l",
  "ł":"l",
  "ǉ":"lj",
  "ſ":"s",
  "ẜ":"s",
  "ẛ":"s",
  "ẝ":"s",
  "ḿ":"m",
  "ṁ":"m",
  "ṃ":"m",
  "ɱ":"m",
  "ᵯ":"m",
  "ᶆ":"m",
  "ń":"n",
  "ň":"n",
  "ņ":"n",
  "ṋ":"n",
  "ȵ":"n",
  "ṅ":"n",
  "ṇ":"n",
  "ǹ":"n",
  "ɲ":"n",
  "ṉ":"n",
  "ƞ":"n",
  "ᵰ":"n",
  "ᶇ":"n",
  "ɳ":"n",
  "ñ":"n",
  "ǌ":"nj",
  "ó":"o",
  "ŏ":"o",
  "ǒ":"o",
  "ô":"o",
  "ố":"o",
  "ộ":"o",
  "ồ":"o",
  "ổ":"o",
  "ỗ":"o",
  "ö":"o",
  "ȫ":"o",
  "ȯ":"o",
  "ȱ":"o",
  "ọ":"o",
  "ő":"o",
  "ȍ":"o",
  "ò":"o",
  "ỏ":"o",
  "ơ":"o",
  "ớ":"o",
  "ợ":"o",
  "ờ":"o",
  "ở":"o",
  "ỡ":"o",
  "ȏ":"o",
  "ꝋ":"o",
  "ꝍ":"o",
  "ⱺ":"o",
  "ō":"o",
  "ṓ":"o",
  "ṑ":"o",
  "ǫ":"o",
  "ǭ":"o",
  "ø":"o",
  "ǿ":"o",
  "õ":"o",
  "ṍ":"o",
  "ṏ":"o",
  "ȭ":"o",
  "ƣ":"oi",
  "ꝏ":"oo",
  "ɛ":"e",
  "ᶓ":"e",
  "ɔ":"o",
  "ᶗ":"o",
  "ȣ":"ou",
  "ṕ":"p",
  "ṗ":"p",
  "ꝓ":"p",
  "ƥ":"p",
  "ᵱ":"p",
  "ᶈ":"p",
  "ꝕ":"p",
  "ᵽ":"p",
  "ꝑ":"p",
  "ꝙ":"q",
  "ʠ":"q",
  "ɋ":"q",
  "ꝗ":"q",
  "ŕ":"r",
  "ř":"r",
  "ŗ":"r",
  "ṙ":"r",
  "ṛ":"r",
  "ṝ":"r",
  "ȑ":"r",
  "ɾ":"r",
  "ᵳ":"r",
  "ȓ":"r",
  "ṟ":"r",
  "ɼ":"r",
  "ᵲ":"r",
  "ᶉ":"r",
  "ɍ":"r",
  "ɽ":"r",
  "ↄ":"c",
  "ꜿ":"c",
  "ɘ":"e",
  "ɿ":"r",
  "ś":"s",
  "ṥ":"s",
  "š":"s",
  "ṧ":"s",
  "ş":"s",
  "ŝ":"s",
  "ș":"s",
  "ṡ":"s",
  "ṣ":"s",
  "ṩ":"s",
  "ʂ":"s",
  "ᵴ":"s",
  "ᶊ":"s",
  "ȿ":"s",
  "ɡ":"g",
  "ᴑ":"o",
  "ᴓ":"o",
  "ᴝ":"u",
  "ť":"t",
  "ţ":"t",
  "ṱ":"t",
  "ț":"t",
  "ȶ":"t",
  "ẗ":"t",
  "ⱦ":"t",
  "ṫ":"t",
  "ṭ":"t",
  "ƭ":"t",
  "ṯ":"t",
  "ᵵ":"t",
  "ƫ":"t",
  "ʈ":"t",
  "ŧ":"t",
  "ᵺ":"th",
  "ɐ":"a",
  "ᴂ":"ae",
  "ǝ":"e",
  "ᵷ":"g",
  "ɥ":"h",
  "ʮ":"h",
  "ʯ":"h",
  "ᴉ":"i",
  "ʞ":"k",
  "ꞁ":"l",
  "ɯ":"m",
  "ɰ":"m",
  "ᴔ":"oe",
  "ɹ":"r",
  "ɻ":"r",
  "ɺ":"r",
  "ⱹ":"r",
  "ʇ":"t",
  "ʌ":"v",
  "ʍ":"w",
  "ʎ":"y",
  "ꜩ":"tz",
  "ú":"u",
  "ŭ":"u",
  "ǔ":"u",
  "û":"u",
  "ṷ":"u",
  "ü":"u",
  "ǘ":"u",
  "ǚ":"u",
  "ǜ":"u",
  "ǖ":"u",
  "ṳ":"u",
  "ụ":"u",
  "ű":"u",
  "ȕ":"u",
  "ù":"u",
  "ủ":"u",
  "ư":"u",
  "ứ":"u",
  "ự":"u",
  "ừ":"u",
  "ử":"u",
  "ữ":"u",
  "ȗ":"u",
  "ū":"u",
  "ṻ":"u",
  "ų":"u",
  "ᶙ":"u",
  "ů":"u",
  "ũ":"u",
  "ṹ":"u",
  "ṵ":"u",
  "ᵫ":"ue",
  "ꝸ":"um",
  "ⱴ":"v",
  "ꝟ":"v",
  "ṿ":"v",
  "ʋ":"v",
  "ᶌ":"v",
  "ⱱ":"v",
  "ṽ":"v",
  "ꝡ":"vy",
  "ẃ":"w",
  "ŵ":"w",
  "ẅ":"w",
  "ẇ":"w",
  "ẉ":"w",
  "ẁ":"w",
  "ⱳ":"w",
  "ẘ":"w",
  "ẍ":"x",
  "ẋ":"x",
  "ᶍ":"x",
  "ý":"y",
  "ŷ":"y",
  "ÿ":"y",
  "ẏ":"y",
  "ỵ":"y",
  "ỳ":"y",
  "ƴ":"y",
  "ỷ":"y",
  "ỿ":"y",
  "ȳ":"y",
  "ẙ":"y",
  "ɏ":"y",
  "ỹ":"y",
  "ź":"z",
  "ž":"z",
  "ẑ":"z",
  "ʑ":"z",
  "ⱬ":"z",
  "ż":"z",
  "ẓ":"z",
  "ȥ":"z",
  "ẕ":"z",
  "ᵶ":"z",
  "ᶎ":"z",
  "ʐ":"z",
  "ƶ":"z",
  "ɀ":"z",
  "ﬀ":"ff",
  "ﬃ":"ffi",
  "ﬄ":"ffl",
  "ﬁ":"fi",
  "ﬂ":"fl",
  "ĳ":"ij",
  "œ":"oe",
  "ﬆ":"st",
  "ₐ":"a",
  "ₑ":"e",
  "ᵢ":"i",
  "ⱼ":"j",
  "ₒ":"o",
  "ᵣ":"r",
  "ᵤ":"u",
  "ᵥ":"v",
  "ₓ":"x"};

export const latinise = (text: string) => {
  return (
    text.replace(/[^A-Za-z0-9\[\] ]/g, (a)=>(latinMap[a]||a))
  )
}

export const countriesUpperLatinised = sortedCountries.map(country => ({...country, description: latinise(country.description.toUpperCase())}))
export const countriesLongText = countriesUpperLatinised.map((country)=>country.description);

export function requiredFalse(): ValidatorFn {
  return (control: AbstractControl): any => {
    return control.value === false ? null : { requiredFalse: true };
  };
}

export function numberValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (control.value === null || control.value === undefined || control.value === '') {
      return null;
    }

    const value = Number(control.value);

    if (isNaN(value)) {
      return { notANumber: true };
    }

    return null;
  };
}
export function greaterThanZeroValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    return control.value > 0 ? null : { greaterThanZero: true };
  };
}

export function greaterOrEqualToZeroValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    return control.value >= 0 ? null : { greaterOrEqualToZero: true };
  };
}

export const  operations = {
    '10': {
      CodOp: 'AIC',
      Start: [
        { locSelectabil: 'codPtf', descriere: 'PTF (Traseul rutier începe într-un punct de frontieră - sens intrare)' },
        { locSelectabil: 'locatie', descriere: 'ADR (Traseul rutier începe într-un loc de pe teritoriul național, altul decât un PTF)' }
      ],
      Final: [
        { locSelectabil: 'codPtf', descriere: 'PTF (Traseul rutier se finalizează într-un punct de frontieră - sens ieșire)' },
        { locSelectabil: 'locatie', descriere: 'ADR (Traseul rutier se finalizează pe teritoriul național)' }
      ]
    },
    '12': {
      CodOp: 'LHI',
      Start: [
        { locSelectabil: 'codPtf', descriere: 'PTF (Traseul rutier începe într-un punct de frontieră - sens intrare)' },
        { locSelectabil: 'locatie', descriere: 'ADR (Traseul rutier începe într-un loc de pe teritoriul național, altul decât un PTF)' }
      ],
      Final: [
        { locSelectabil: 'locatie', descriere: 'ADR (Traseul rutier se finalizează pe teritoriul național)' }
      ]
    },
    '14': {
      CodOp: 'SCI',
      Start: [
        { locSelectabil: 'codPtf', descriere: 'PTF (Traseul rutier începe într-un punct de frontieră - sens intrare)' },
        { locSelectabil: 'locatie', descriere: 'ADR (Traseul rutier începe într-un loc de pe teritoriul național, altul decât un PTF)' }
      ],
      Final: [
        { locSelectabil: 'locatie', descriere: 'ADR (Traseul rutier se finalizează pe teritoriul național)' }
      ]
    },
    '20': {
      CodOp: 'LIC',
      Start: [
        { locSelectabil: 'locatie', descriere: 'ADR (Traseul rutier începe pe teritoriul național)' }
      ],
      Final: [
        { locSelectabil: 'codPtf', descriere: 'PTF (Traseul rutier se finalizează într-un punct de frontieră - sens ieșire)' },
        { locSelectabil: 'locatie', descriere: 'ADR (Traseul rutier se finalizează într-un loc de pe teritoriul național, altul decât un PTF)' }
      ]
    },
    '22': {
      CodOp: 'LHE',
      Start: [
        { locSelectabil: 'locatie', descriere: 'ADR (Traseul rutier începe pe teritoriul național)' }
      ],
      Final: [
        { locSelectabil: 'codPtf', descriere: 'PTF (Traseul rutier se finalizează într-un punct de frontieră - sens ieșire)' },
        { locSelectabil: 'locatie', descriere: 'ADR (Traseul rutier se finalizează într-un loc de pe teritoriul național, altul decât un PTF)' }
      ]
    },
    '24': {
      CodOp: 'SCE',
      Start: [
        { locSelectabil: 'locatie', descriere: 'ADR (Traseul rutier începe pe teritoriul național)' }
      ],
      Final: [
        { locSelectabil: 'codPtf', descriere: 'PTF (Traseul rutier se finalizează într-un punct de frontieră - sens ieșire)' },
        { locSelectabil: 'locatie', descriere: 'ADR (Traseul rutier se finalizează într-un loc de pe teritoriul național, altul decât un PTF)' }
      ]
    },
    '30': {
      CodOp: 'TTN',
      Start: [
        { locSelectabil: 'locatie', descriere: 'ADR (Traseul rutier începe pe teritoriul național)' }
      ],
      Final: [
        { locSelectabil: 'locatie', descriere: 'ADR (Traseul rutier se finalizează pe teritoriul național)' }
      ]
    },
    '40': {
      CodOp: 'IMP',
      Start: [
        { locSelectabil: 'codPtf', descriere: 'PTF (Formalitățile vamale au loc în afara teritoriului național)' },
        { locSelectabil: 'codBirouVamal', descriere: 'BV (Formalitățile vamale au loc pe teritoriul național, la un birou vamal de import)' },
        { locSelectabil: 'locatie', descriere: 'ADR (Formalitățile vamale au loc pe teritoriul național, în alt loc decât la un birou vamal)' }
      ],
      Final: [
        { locSelectabil: 'locatie', descriere: 'ADR (Traseul rutier se finalizează pe teritoriul național)' }
      ]
    },
    '50': {
      CodOp: 'EXP',
      Start: [
        { locSelectabil: 'locatie', descriere: 'ADR (Traseul rutier începe pe teritoriul național)' }
      ],
      Final: [
        { locSelectabil: 'codPtf', descriere: 'PTF (Formalitățile vamale au loc în afara teritoriului național)' },
        { locSelectabil: 'codBirouVamal', descriere: 'BV (Formalitățile vamale au loc pe teritoriul național, la un birou vamal de export)' },
        { locSelectabil: 'locatie', descriere: 'ADR (Formalitățile vamale au loc pe teritoriul național, în alt loc decât la un birou vamal)' }
      ]
    },
    '60': {
      CodOp: 'DIN',
      Start: [
        { locSelectabil: 'codPtf', descriere: 'PTF (Traseul rutier începe într-un punct de frontieră - sens intrare)' },
        { locSelectabil: 'locatie', descriere: 'ADR (Traseul rutier începe într-un loc de pe teritoriul național, altul decât un PTF)' }
      ],
      Final: [
        { locSelectabil: 'locatie', descriere: 'ADR (Traseul rutier se finalizează pe teritoriul național)' }
      ]
    },
    '70': {
      CodOp: 'DIE',
      Start: [
        { locSelectabil: 'locatie', descriere: 'ADR (Traseul rutier începe pe teritoriul național)' }
      ],
      Final: [
        { locSelectabil: 'codPtf', descriere: 'PTF (Traseul rutier se finalizează într-un punct de frontieră - sens ieșire)' },
        { locSelectabil: 'locatie', descriere: 'ADR (Traseul rutier se finalizează într-un loc de pe teritoriul național, altul decât un PTF)' }
      ]
    }
  };

export const coduriTipOperatiune = [
  { value: "10", description: "AIC - Achiziţie intracomunitară" },
  { value: "12", description: "LHI - Operațiuni în sistem lohn (UE) - intrare" },
  { value: "14", description: "SCI - Stocuri la dispoziția clientului (Call-off stock) - intrare" },
  { value: "20", description: "LIC - Livrare intracomunitară" },
  { value: "22", description: "LHE - Operațiuni în sistem lohn (UE) - ieșire" },
  { value: "24", description: "SCE - Stocuri la dispoziția clientului (Call-off stock) - ieșire" },
  { value: "30", description: "TTN - Transport pe teritoriul naţional" },
  { value: "40", description: "IMP - Import" },
  { value: "50", description: "EXP - Export" },
  { value: "60", description: "DIN - Tranzacţie intracomunitară - Intrare pentru depozitare/formare nou transport" },
  { value: "70", description: "DIE - Tranzacţie intracomunitară - Ieşire după depozitare/formare nou transport" },
];

export const coduriScopOperatiune = {
  '10': [
    { value: '101', description: "Comercializare" },
    { value: '201', description: "Producție" },
    { value: '301', description: "Gratuități" },
    { value: '401', description: "Echipament comercial" },
    { value: '501', description: "Mijloace fixe" },
    { value: '601', description: "Consum propriu" },
    { value: '703', description: "Operațiuni de livrare cu instalare" },
    { value: '801', description: "Leasing financiar/operațional" },
    { value: '802', description: "Bunuri în garanție" },
    { value: '901', description: "Operațiuni scutite" },
    { value: '1001', description: "Investiție in curs" },
    { value: '1101', description: "Donații, ajutoare" },
    { value: '9901', description: "Altele" }
  ],
  '12': [
    { value: '9999', description: "Același cu operațiunea" }
  ],
  '14': [
    { value: '9999', description: "Același cu operațiunea" }
  ],
  '20': [
    { value: '101', description: "Comercializare" },
    { value: '301', description: "Gratuități" },
    { value: '703', description: "Operațiuni de livrare cu instalare" },
    { value: '801', description: "Leasing financiar/operațional" },
    { value: '802', description: "Bunuri în garanție" },
    { value: '9901', description: "Altele" }
  ],
  '22': [
    { value: '9999', description: "Același cu operațiunea" }
  ],
  '24': [
    { value: '9999', description: "Același cu operațiunea" }
  ],
  '30': [
    { value: '101', description: "Comercializare" },
    { value: '704', description: "Transfer între gestiuni" },
    { value: '705', description: "Bunuri puse la dispoziția clientului" },
    { value: '9901', description: "Altele" }
  ],
  '40': [
    { value: '9999', description: "Același cu operațiunea" }
  ],
  '50': [
    { value: '9999', description: "Același cu operațiunea" }
  ],
  '60': [
    { value: '9999', description: "Același cu operațiunea" }
  ],
  '70': [
    { value: '9999', description: "Același cu operațiunea" }
  ]
};

export const tipDocumente = [
  { value: '10', description: "CMR" },
  { value: '20', description: "Factura" },
  { value: '30', description: "Aviz de însoțire a mărfii" },
  { value: '9999', description: "Altele" }
];

export const codPtfOptions = [
  { value: '1', description: 'Petea (HU)' },
  { value: '2', description: 'Borș (HU)' },
  { value: '3', description: 'Vărșand (HU)' },
  { value: '4', description: 'Nădlac (HU)' },
  { value: '5', description: 'Calafat (BG)' },
  { value: '6', description: 'Bechet (BG)' },
  { value: '7', description: 'Turnu Măgurele (BG)' },
  { value: '8', description: 'Zimnicea (BG)' },
  { value: '9', description: 'Giurgiu (BG)' },
  { value: '10', description: 'Ostrov (BG)' },
  { value: '11', description: 'Negru Vodă (BG)' },
  { value: '12', description: 'Vama Veche (BG)' },
  { value: '13', description: 'Călărași (BG)' },
  { value: '14', description: 'Corabia (BG)' },
  { value: '15', description: 'Oltenița (BG)' },
  { value: '16', description: 'Carei (HU)' },
  { value: '17', description: 'Cenad (HU)' },
  { value: '18', description: 'Episcopia Bihor (HU)' },
  { value: '19', description: 'Salonta (HU)' },
  { value: '20', description: 'Săcuieni (HU)' },
  { value: '21', description: 'Turnu (HU)' },
  { value: '22', description: 'Urziceni (HU)' },
  { value: '23', description: 'Valea lui Mihai (HU)' },
  { value: '24', description: 'Vladimirescu (HU)' },
  { value: '25', description: 'Porțile de Fier 1 (RS)' },
  { value: '26', description: 'Naidăș (RS)' },
  { value: '27', description: 'Stamora Moravița (RS)' },
  { value: '28', description: 'Jimbolia (RS)' },
  { value: '29', description: 'Halmeu (UA)' },
  { value: '30', description: 'Stânca Costești (MD)' },
  { value: '31', description: 'Sculeni (MD)' },
  { value: '32', description: 'Albița (MD)' },
  { value: '33', description: 'Oancea (MD)' },
  { value: '34', description: 'Galați Giurgiulești (MD)' },
  { value: '35', description: 'Constanța Sud Agigea (-)' },
  { value: '36', description: 'Siret (UA)' },
  { value: '37', description: 'Nădlac 2 - A1 (HU)' },
  { value: '38', description: 'Borș 2 - A3 (HU)' }
].sort((a, b) => {
    if (a.description < b.description) {
        return -1;
    }
    if (a.description > b.description) {
        return 1;
    }
    return 0;
  });

  export const codBirouVamalOptions = [
  { value: '12801', description: "BVI Alba Iulia (ROBV0300)" },
  { value: '22801', description: "BVI Arad (ROTM0200)" },
  { value: '22901', description: "BVF Arad Aeroport (ROTM0230)" },
  { value: '22902', description: "BVF Zona Liberă Curtici (ROTM2300)" },
  { value: '32801', description: "BVI Pitești (ROCR7000)" },
  { value: '42801', description: "BVI Bacău (ROIS0600)" },
  { value: '42901', description: "BVF Bacău Aeroport (ROIS0620)" },
  { value: '52801', description: "BVI Oradea (ROCJ6570)" },
  { value: '52901', description: "BVF Oradea Aeroport (ROCJ6580)" },
  { value: '62801', description: "BVI Bistriţa-Năsăud (ROCJ0400)" },
  { value: '72801', description: "BVI Botoşani (ROIS1600)" },
  { value: '72901', description: "BVF Stanca Costeşti (ROIS1610)" },
  { value: '72902', description: "BVF Rădăuţi Prut (ROIS1620)" },
  { value: '82801', description: "BVI Braşov (ROBV0900)" },
  { value: '92901', description: "BVF Zona Liberă Brăila (ROGL0710)" },
  { value: '92902', description: "BVF Brăila (ROGL0700)" },
  { value: '102801', description: "BVI Buzău (ROGL1500)" },
  { value: '112801', description: "BVI Reșița (ROTM7600)" },
  { value: '112901', description: "BVF Naidăș (ROTM6100)" },
  { value: '122801', description: "BVI Cluj Napoca (ROCJ1800)" },
  { value: '122901', description: "BVF Cluj Napoca Aero (ROCJ1810)" },
  { value: '132901', description: "BVF Constanţa Sud Agigea (ROCT1900)" },
  { value: '132902', description: "BVF Mihail Kogălniceanu (ROCT5100)" },
  { value: '132903', description: "BVF Mangalia (ROCT5400)" },
  { value: '132904', description: "BVF Constanţa Port (ROCT1970)" },
  { value: '142801', description: "BVI Sfântu Gheorghe (ROBV7820)" },
  { value: '152801', description: "BVI Târgoviște (ROBU8600)" },
  { value: '162801', description: "BVI Craiova (ROCR2100)" },
  { value: '162901', description: "BVF Craiova Aeroport (ROCR2110)" },
  { value: '162902', description: "BVF Bechet (ROCR1720)" },
  { value: '162903', description: "BVF Calafat (ROCR1700)" },
  { value: '172901', description: "BVF Zona Liberă Galaţi (ROGL3810)" },
  { value: '172902', description: "BVF Giurgiuleşti (ROGL3850)" },
  { value: '172903', description: "BVF Oancea (ROGL3610)" },
  { value: '172904', description: "BVF Galaţi (ROGL3800)" },
  { value: '182801', description: "BVI Târgu Jiu (ROCR8810)" },
  { value: '192801', description: "BVI Miercurea Ciuc (ROBV5600)" },
  { value: '202801', description: "BVI Deva (ROTM8100)" },
  { value: '212801', description: "BVI Slobozia (ROCT8220)" },
  { value: '222901', description: "BVF Iaşi Aero (ROIS4660)" },
  { value: '222902', description: "BVF Sculeni (ROIS4990)" },
  { value: '222903', description: "BVF Iaşi (ROIS4650)" },
  { value: '232801', description: "BVI Antrepozite/Ilfov (ROBU1200)" },
  { value: '232901', description: "BVF Otopeni Călători (ROBU1030)" },
  { value: '242801', description: "BVI Baia Mare (ROCJ0500)" },
  { value: '242901', description: "BVF Aero Baia Mare (ROCJ0510)" },
  { value: '242902', description: "BVF Sighet (ROCJ8000)" },
  { value: '252901', description: "BVF Orşova (ROCR7280)" },
  { value: '252902', description: "BVF Porţile De Fier I (ROCR7270)" },
  { value: '252903', description: "BVF Porţile De Fier II (ROCR7200)" },
  { value: '252904', description: "BVF Drobeta Turnu Severin (ROCR9000)" },
  { value: '262801', description: "BVI Târgu Mureş (ROBV8800)" },
  { value: '262901', description: "BVF Târgu Mureş Aeroport (ROBV8820)" },
  { value: '272801', description: "BVI Piatra Neamţ (ROIS7400)" },
  { value: '282801', description: "BVI Corabia (ROCR2000)" },
  { value: '282802', description: "BVI Olt (ROCR8210)" },
  { value: '292801', description: "BVI Ploiești (ROBU7100)" },
  { value: '302801', description: "BVI Satu-Mare (ROCJ7810)" },
  { value: '302901', description: "BVF Halmeu (ROCJ4310)" },
  { value: '302902', description: "BVF Aeroport Satu Mare (ROCJ7830)" },
  { value: '312801', description: "BVI Zalău (ROCJ9700)" },
  { value: '322801', description: "BVI Sibiu (ROBV7900)" },
  { value: '322901', description: "BVF Sibiu Aeroport (ROBV7910)" },
  { value: '332801', description: "BVI Suceava (ROIS8230)" },
  { value: '332901', description: "BVF Dorneşti (ROIS2700)" },
  { value: '332902', description: "BVF Siret (ROIS8200)" },
  { value: '332903', description: "BVF Suceava Aero (ROIS8250)" },
  { value: '332904', description: "BVF Vicovu De Sus (ROIS9620)" },
  { value: '342801', description: "BVI Alexandria (ROCR0310)" },
  { value: '342901', description: "BVF Turnu Măgurele (ROCR9100)" },
  { value: '342902', description: "BVF Zimnicea (ROCR5800)" },
  { value: '352802', description: "BVI Timişoara Bază (ROTM8720)" },
  { value: '352901', description: "BVF Jimbolia (ROTM5010)" },
  { value: '352902', description: "BVF Moraviţa (ROTM5510)" },
  { value: '352903', description: "BVF Timişoara Aeroport (ROTM8730)" },
  { value: '362901', description: "BVF Sulina (ROCT8300)" },
  { value: '362902', description: "BVF Aeroport Delta Dunării Tulcea (ROGL8910)" },
  { value: '362903', description: "BVF Tulcea (ROGL8900)" },
  { value: '362904', description: "BVF Isaccea (ROGL8920)" },
  { value: '372801', description: "BVI Vaslui (ROIS9610)" },
  { value: '372901', description: "BVF Fălciu (-)" },
  { value: '372902', description: "BVF Albiţa (ROIS0100)" },
  { value: '382801', description: "BVI Râmnicu Vâlcea (ROCR7700)" },
  { value: '392801', description: "BVI Focșani (ROGL3600)" },
  { value: '402801', description: "BVI Bucureşti Poştă (ROBU1380)" },
  { value: '402802', description: "BVI Târguri și Expoziții (ROBU1400)" },
  { value: '402901', description: "BVF Băneasa (ROBU1040)" },
  { value: '512801', description: "BVI Călăraşi (ROCT1710)" },
  { value: '522801', description: "BVI Giurgiu (ROBU3910)" },
  { value: '522901', description: "BVF Zona Liberă Giurgiu (ROBU3980)" },
].sort((a, b) => {
  if (a.description < b.description) {
      return -1;
  }
  if (a.description > b.description) {
      return 1;
  }
  return 0;
});

export const coduriJudete = [
  { value: '1', description: "Alba" },
  { value: '2', description: "Arad" },
  { value: '3', description: "Argeş" },
  { value: '4', description: "Bacău" },
  { value: '5', description: "Bihor" },
  { value: '6', description: "Bistriţa-Năsăud" },
  { value: '7', description: "Botoşani" },
  { value: '8', description: "Braşov" },
  { value: '9', description: "Brăila" },
  { value: '10', description: "Buzău" },
  { value: '11', description: "Caraş-Severin" },
  { value: '51', description: "Călăraşi" },
  { value: '12', description: "Cluj" },
  { value: '13', description: "Constanţa" },
  { value: '14', description: "Covasna" },
  { value: '15', description: "Dâmboviţa" },
  { value: '16', description: "Dolj" },
  { value: '17', description: "Galaţi" },
  { value: '52', description: "Giurgiu" },
  { value: '18', description: "Gorj" },
  { value: '19', description: "Harghita" },
  { value: '20', description: "Hunedoara" },
  { value: '21', description: "Ialomiţa" },
  { value: '22', description: "Iaşi" },
  { value: '23', description: "Ilfov" },
  { value: '24', description: "Maramureş" },
  { value: '25', description: "Mehedinţi" },
  { value: '40', description: "Municipiul Bucureşti" },
  { value: '26', description: "Mureş" },
  { value: '27', description: "Neamţ" },
  { value: '28', description: "Olt" },
  { value: '29', description: "Prahova" },
  { value: '30', description: "Satu Mare" },
  { value: '31', description: "Sălaj" },
  { value: '32', description: "Sibiu" },
  { value: '33', description: "Suceava" },
  { value: '34', description: "Teleorman" },
  { value: '35', description: "Timiş" },
  { value: '36', description: "Tulcea" },
  { value: '37', description: "Vaslui" },
  { value: '38', description: "Vâlcea" },
  { value: '39', description: "Vrancea" },
].sort((a, b) => {
  if (a.description < b.description) {
      return -1;
  }
  if (a.description > b.description) {
      return 1;
  }
  return 0;
});

export function makeBold(string, search, onlyStart = false){
  const reg = new RegExp((onlyStart ? '^' : '' ) + '(' + search + ')', onlyStart ? '' : 'ig');
  return string.replace(reg, '<b class="found">$1</b>');
}

export function toNumber(string, defaultValue = 0) {
  const num = Number(string)
  if (isNaN(num)) {
    return defaultValue
  }
  return num
}

export function movementTypeToLabel(type) {
  let label = type;
  switch (type) {
    case 'intrare':
      label = 'Intrare';
      break;
    case 'intrarePVA':
      label = 'Intrare PVA';
      break;  
    case 'stocInitial':
      label = 'Stoc Inițial';
      break;
    case 'factura':
      label = 'Factură';
      break;
    case 'aviz':
      label = 'Aviz';
      break;
    case 'bonConsum':
      label = 'Bon de consum';
      break;
    case 'transferIesire':
      label = 'Transfer Ieșire';
      break;
    case 'transferIntrare':
      label = 'Transfer Intrare';
      break;
    case 'inventariereIesire':
      label = 'Inventariere (iesire)';
      break;
    case 'inventariereIntrare':
      label = 'Inventariere (intrare)';
      break;
    case 'incarcareStoc':
      label = 'Încărcare stoc';
      break;
    case 'descarcareStoc':
      label = 'Descărcare stoc';
      break;
    case 'pv':
      label = 'Proces Verbal Modificare de Preț';
      break;
    default: break;
  }
  return label;
}

export function gestiuneTypeToLabel(type) {
  let label = '';
  switch (type) {
    case '0':
      label = 'Amănunt - Cantitativ Valoric';
      break;
    case '1':
      label = 'Amănunt - Global Valoric';
      break;
    case '2':
      label = 'En-Gros - Cantitativ Valoric';
      break;
    default: break;
  }
  return label;
}
export const acceptedChatFileTypes = [
  'image/png',
  'image/jpeg',
  'application/pdf',
  'video/mp4',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/zip',
  'application/x-zip-compressed',
  'application/vnd.rar',
  'application/x-compressed',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
]

/**
 * Generic canActivate function to verify access via backend call.
 *
 * @param config {
 *   paramKey: string - name of route param (e.g. 'id'),
 *   endpoint: string - API endpoint with `:paramKey` placeholder (e.g. '/api/resource/:id/verify'),
 *   checkProperty: string - property in response to check (e.g. 'owner'),
 *   redirectTo: string - fallback route on failure (e.g. '/unauthorized')
 * }
 */
export function canActivateWithBackend(config: {
  paramKey: string;
  endpoint: string;
  checkProperty: string;
  redirectTo: string;
}): CanActivateFn {
  return (route: ActivatedRouteSnapshot) => {
    const SERVER_URL = `${environment.API_URL}`;

    const http = inject(HttpClient);
    const router = inject(Router);

    const paramValue = route.params[config.paramKey];
    const url = config.endpoint.replace(`:${config.paramKey}`, paramValue);

    return http.get(SERVER_URL + url).pipe(
      map((res: any) => {
        const valid = res?.[config.checkProperty] === true;
        if (!valid) {
          router.navigate([config.redirectTo]);
        }
        return valid;
      }),
      catchError(() => {
        router.navigate([config.redirectTo]);
        return of(false);
      })
    );
  };
}

export function ibanValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const rawValue = control.value;

    if (!rawValue) {
      return null;
    }

    const iban = rawValue.toString().toUpperCase().replace(/\s+/g, '');

    const ibanRegex = /^[A-Z]{2}\d{2}[A-Z0-9]{11,30}$/;
    if (!ibanRegex.test(iban)) {
      return { iban: { invalidFormat: true } };
    }

    if (!isValidIbanChecksum(iban)) {
      return { iban: { invalidChecksum: true } };
    }

    return null;
  };
}

function isValidIbanChecksum(iban: string): boolean {
  const rearranged = iban.slice(4) + iban.slice(0, 4);

  const numericString = rearranged
    .split('')
    .map(char => {
      const code = char.charCodeAt(0);
      return code >= 65 && code <= 90 ? (code - 55).toString() : char;
    })
    .join('');

  let remainder = numericString;
  while (remainder.length > 2) {
    const chunk = remainder.slice(0, 9);
    remainder = (parseInt(chunk, 10) % 97).toString() + remainder.slice(chunk.length);
  }

  return parseInt(remainder, 10) % 97 === 1;
}

export function bicValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const rawValue = control.value;

    if (!rawValue) {
      return null;
    }

    const bic = rawValue.toString().toUpperCase().trim();

    const bicRegex = /^[A-Z]{4}[A-Z]{2}[A-Z0-9]{2}([A-Z0-9]{3})?$/;
    if (!bicRegex.test(bic)) {
      return { bic: { invalidFormat: true } };
    }

    return null;
  };
}

export function beneficiaryNameValidator(
  minLength = 2,
  maxLength = 70
): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const rawValue = control.value;

    if (!rawValue) {
      return null;
    }

    const name = rawValue.toString().trim();

    if (name.length === 0) {
      return { beneficiaryName: { whitespaceOnly: true } };
    }

    if (name.length < minLength) {
      return { beneficiaryName: { tooShort: true, requiredLength: minLength } };
    }

    if (name.length > maxLength) {
      return { beneficiaryName: { tooLong: true, maxLength } };
    }

    const nameRegex = /^[A-Za-zÀ-ÖØ-öø-ÿ0-9 .,'\-&/]+$/;
    if (!nameRegex.test(name)) {
      return { beneficiaryName: { invalidCharacters: true } };
    }

    return null;
  };
}

const CNP_CONTROL_KEY = [2, 7, 9, 1, 4, 6, 3, 5, 8, 2, 7, 9];

// Valid county codes: 01-46 (counties), 51-52 (Bucharest sectors)
const VALID_COUNTY_CODES = new Set([
  ...Array.from({ length: 46 }, (_, i) => (i + 1).toString().padStart(2, '0')),
  '51', '52',
]);

export function cnpValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const rawValue = control.value;

    if (!rawValue) {
      return null;
    }

    const cnp = rawValue.toString().trim();

    if (!/^\d{13}$/.test(cnp)) {
      return { cnp: { invalidFormat: true } };
    }

    const sDigit = parseInt(cnp[0], 10);
    if (sDigit < 1 || sDigit > 9) {
      return { cnp: { invalidSexDigit: true } };
    }

    const century = getCenturyFromSexDigit(sDigit);
    if (century === null) {
      return { cnp: { invalidSexDigit: true } };
    }

    const year = century + parseInt(cnp.slice(1, 3), 10);
    const month = parseInt(cnp.slice(3, 5), 10);
    const day = parseInt(cnp.slice(5, 7), 10);

    if (!isValidDate(year, month, day)) {
      return { cnp: { invalidDate: true } };
    }

    const countyCode = cnp.slice(7, 9);
    if (!VALID_COUNTY_CODES.has(countyCode)) {
      return { cnp: { invalidCountyCode: true } };
    }

    if (!isValidChecksum(cnp)) {
      return { cnp: { invalidChecksum: true } };
    }

    return null;
  };
}

function getCenturyFromSexDigit(sDigit: number): number | null {
  switch (sDigit) {
    case 1:
    case 2:
      return 1900;
    case 3:
    case 4:
      return 1800;
    case 5:
    case 6:
      return 2000;
    case 7:
    case 8:
      return 1900;
    case 9:
      return 1900;
    default:
      return null;
  }
}

function isValidDate(year: number, month: number, day: number): boolean {
  if (month < 1 || month > 12) return false;
  if (day < 1 || day > 31) return false;

  const date = new Date(year, month - 1, day);
  return (
    date.getFullYear() === year &&
    date.getMonth() === month - 1 &&
    date.getDate() === day &&
    year >= 1800 &&
    year <= new Date().getFullYear()
  );
}

function isValidChecksum(cnp: string): boolean {
  const digits = cnp.split('').map(Number);
  const sum = CNP_CONTROL_KEY.reduce(
    (acc, key, i) => acc + key * digits[i],
    0
  );

  const remainder = sum % 11;
  const checkDigit = remainder === 10 ? 1 : remainder;

  return checkDigit === digits[12];
}
